import { convertTypesToLetters } from "./categoryBucket";
import type { SaveUserPreferencesPayload, UserPreferences } from "./types";

/**
 * Optimistically merges a save-preferences payload back into the cached UserPreferences object.
 * This allows bfcache-restored pages to see the freshly-submitted values without a network
 * round-trip (invalidation doesn't fire after bfcache restore).
 *
 * Returns null when the cached value is null (institutional users or failed initial fetch —
 * no cache to update, so skip the optimistic write).
 */
export const mergePayloadIntoPreferences = (
    cached: UserPreferences | null,
    payload: SaveUserPreferencesPayload,
): UserPreferences | null => {
    if (cached === null) return null;

    const prefs = { ...cached, UserPreferences: { ...cached.UserPreferences } };

    if (payload.givenNameExactness) {
        prefs.UserPreferences.FirstNameExactness = {
            Exact: payload.givenNameExactness.isExact,
            Phonetic: payload.givenNameExactness.flags.phonetic,
            Initials: payload.givenNameExactness.flags.initials,
            Similar: payload.givenNameExactness.flags.similar,
        };
    }

    if (payload.surnameExactness) {
        prefs.UserPreferences.LastNameExactness = {
            Exact: payload.surnameExactness.isExact,
            Phonetic: payload.surnameExactness.flags.phonetic,
            Similar: payload.surnameExactness.flags.similar,
            Soundex: payload.surnameExactness.flags.soundex,
        };
    }

    if (payload.types) {
        prefs.UserPreferences.CategoryBucket = convertTypesToLetters(payload.types);
    }

    if (payload.collectionFocus !== undefined) {
        prefs.UserPreferences.CollectionFocus = payload.collectionFocus;
    }

    if (payload.showUnviewedRecordsOnly !== undefined) {
        prefs.UserPreferences.ShowUnviewedRecordsOnly = payload.showUnviewedRecordsOnly;
    }

    if (payload.showMoreOptions !== undefined) {
        prefs.UserPreferences.ShowMoreOptions = payload.showMoreOptions;
    }

    if (payload.matchAllTermsExactly !== undefined) {
        prefs.UserPreferences.MatchAllTermsExactly = payload.matchAllTermsExactly;
    }

    if (payload.isSkipAssistPlacesModalPermanently !== undefined) {
        prefs.UserPreferences.IsSkipAssistPlacesModalPermanently = payload.isSkipAssistPlacesModalPermanently;
    }

    return prefs;
};
